import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';

import { AREE, C, T, MONO } from './data/aree.ts';
import type { Blocco, Cella, Tono } from './data/aree.ts';
import { PANNELLI } from './data/pannelli.ts';
import { PannelloLavoro } from './PannelloLavoro.tsx';

/** Colore del TESTO per un tono. I valori di riempimento (#1d6b48, #e8b829)
 *  non passano 4,5:1 come testo: qui servono le varianti scure. */
function coloreTesto(t: Tono): string {
  return t === 'ko' ? T.ko : t === 'att' ? T.att : '#3d4451';
}

function Cellula({ cella }: { readonly cella: Cella }): JSX.Element {
  const [testo, tono] = cella;
  return (
    <td style={{
      color: coloreTesto(tono),
      fontFamily: tono === 'mono' ? MONO : 'inherit',
      fontSize: tono === 'mono' ? '12.5px' : '13.5px',
    }}>{testo}</td>
  );
}

function BloccoArea({ bl }: { readonly bl: Blocco }): JSX.Element {
  const tipo = bl.tipo ?? 'tabella';
  return (
    <section className="uc-blocco">
      <header>
        <div>
          <h2>{bl.titolo}</h2>
          <div className="uc-blocco-nota">{bl.nota}</div>
        </div>
        {bl.azione && <button type="button" className="uc-btn secondario">{bl.azione}</button>}
      </header>

      {tipo === 'tabella' && (
        <div className="uc-scroll">
          <table className="uc-tab">
            <thead>
              <tr>{bl.colonne?.map((c, i) => <th key={c + String(i)}>{c}</th>)}</tr>
            </thead>
            <tbody>
              {bl.righe?.map((r) => (
                <tr key={r.nome}>
                  <td>
                    <div className="uc-cella-nome">
                      <span className="uc-punto" style={{ background: C[r.stato] }} aria-hidden="true" />
                      <div>
                        <div className="uc-nome">{r.nome}</div>
                        <div className="uc-sotto">{r.sotto}</div>
                      </div>
                    </div>
                  </td>
                  {r.celle.map((c, i) => <Cellula key={String(i)} cella={c} />)}
                  <td className="uc-azione"><a href="#area">{r.azione}</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tipo === 'schede' && (
        <div className="uc-schede">
          {bl.schede?.map(([titolo, valore, nota, tono]) => (
            <div className="uc-scheda" key={titolo}>
              <div className="uc-scheda-testa">
                <span className="uc-punto" style={{ background: C[tono] }} aria-hidden="true" />
                <span className="uc-scheda-titolo">{titolo}</span>
              </div>
              <div className="uc-scheda-valore mono">{valore}</div>
              <div className="uc-scheda-nota">{nota}</div>
            </div>
          ))}
        </div>
      )}

      {tipo === 'lista' && (
        <div className="uc-lista">
          {bl.voci?.map(([nome, nota, valore, tono]) => (
            <div className="uc-voce" key={nome}>
              <div>
                <div className="uc-nome">{nome}</div>
                <div className="uc-sotto">{nota}</div>
              </div>
              <div className="mono uc-voce-valore" style={{ color: tono === 'ko' ? T.ko : tono === 'att' ? T.att : '#1b2230' }}>
                {valore}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * UNA vista per tutte le 13 aree. Il contenuto arriva da data/aree.ts e il
 * pannello di lavoro da data/pannelli.ts.
 *
 * Ordine deliberato: intestazione → KPI → AVVISO di ciò che non va → pannello
 * di lavoro → tabelle. Chi apre un'area deve vedere prima il problema, poi il
 * comando per risolverlo, e solo dopo l'inventario. L'inverso trasforma un
 * cruscotto in un archivio.
 */
export function VistaArea({ id, onAiuto }: { readonly id: string; readonly onAiuto: () => void }): JSX.Element | null {
  const area = AREE[id];
  const pannello = PANNELLI[id];
  if (!area) return null;

  return (
    <>
      <div className="uc-area-testa">
        <div>
          <div className="uc-gruppo">{area.gruppo}</div>
          <h1>{area.titolo}</h1>
          <p className="lede">{area.lede}</p>
        </div>
        <div className="uc-area-azioni">
          {area.azioni.map(([nome, genere, href]) => {
            const cls = 'uc-btn ' + (genere === 'primaria' ? 'primario' : 'secondario');
            // Con href è un Link: una voce che porta altrove non deve essere un
            // bottone, e mai un'assegnazione a window.location.
            return href
              ? <Link key={nome} to={href} className={cls}>{nome}</Link>
              : <button key={nome} type="button" className={cls}>{nome}</button>;
          })}
        </div>
      </div>

      <div className="uc-kpi">
        {area.kpi.map(([etichetta, valore, nota, tono]) => (
          <div className="uc-kpi-card" key={etichetta}>
            <div className="uc-kpi-et">{etichetta}</div>
            <div className="uc-kpi-val" style={{ color: tono === 'ko' ? T.ko : tono === 'att' ? T.att : '#1b2230' }}>
              {valore}
            </div>
            <div className="uc-kpi-nota">{nota}</div>
          </div>
        ))}
      </div>

      {area.avvisi.map(([gravita, titolo, testo, azione]) => (
        <div className="uc-avviso" key={titolo} style={{ borderLeftColor: gravita === 'ko' ? C.ko : C.att }}>
          <div>
            <div className="uc-avviso-titolo">{titolo}</div>
            <div className="uc-avviso-testo">{testo}</div>
          </div>
          <div className="uc-avviso-azioni">
            <button type="button" className="uc-btn primario">{azione}</button>
            {/* Porta alla Risoluzione assistita: il problema è già in contesto. */}
            <button type="button" className="uc-btn secondario ia" onClick={onAiuto}>Chiedi aiuto</button>
          </div>
        </div>
      ))}

      {pannello && <PannelloLavoro pannello={pannello} />}

      {area.blocchi.map((bl) => <BloccoArea key={bl.titolo} bl={bl} />)}
    </>
  );
}
